// HowItWorks.jsx
import React, { useMemo, useRef, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import LifeBalancePhone from "../Images/Accueil/macbookfeed.png";

import "../Style/howItWorks.css";
import "../Style/Accueil.ObsidianAura.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}
function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}
function normalizeMaybeIndexedObjectToArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    const keys = Object.keys(value)
      .filter((k) => String(Number(k)) === k)
      .sort((a, b) => Number(a) - Number(b));
    if (keys.length) return keys.map((k) => value[k]);
  }
  return [];
}

export default function HowItWorks() {
  const { t, i18n } = useTranslation("howItWorks");

  // i18n data
  const hero = asObject(t("hero", { returnObjects: true }));
  const rawSteps = t("step", { returnObjects: true });
  const steps = useMemo(
    () => normalizeMaybeIndexedObjectToArray(rawSteps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, rawSteps]
  );

  const labels = asObject(t("labels", { returnObjects: true }));
  const simplicity = asObject(t("simplicity_block", { returnObjects: true }));
  const principlesTitle = t("principles_title", "Pourquoi c’est simple");
  const principlesSubtitle = t("principles_subtitle", "");
  const principles = asArray(t("principles", { returnObjects: true }));
  const weekTitle = t("week_title", "Ce que tu obtiens en 7 jours");
  const weekSubtitle = t("week_subtitle", "");
  const week = asArray(t("week_in_7_days", { returnObjects: true }));
  const closing = asObject(t("closing", { returnObjects: true }));

  // UI state
  const sectionRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const navItems = useMemo(() => {
    return steps.map((s, i) => ({
      id: s?.id || `step-${i + 1}`,
      label: s?.short_title || t("fallback.step_title", "Étape"),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, i18n.language]);

  useEffect(() => {
    if (!steps.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0))[0];

        if (!visible) return;

        const idx = Number(visible.target.getAttribute("data-step-index"));
        if (!Number.isNaN(idx)) setActiveIndex(idx);
      },
      { root: null, rootMargin: "-35% 0px -55% 0px", threshold: [0.1, 0.2, 0.35, 0.5, 0.7] }
    );

    sectionRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [steps.length]);

  function scrollToStep(i) {
    const el = sectionRefs.current[i];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <main className="life-home hiw">
      <Helmet>
              <title>{"Comment fonctionne Life"}</title>
              <meta name="title" content={"Life - How it works"} />
              <link rel="canonical" href="https://be-lifer.com/how-it-works" />
              <meta name="author" content="Be-lifer" />
              <meta property="og:type" content="website" />
              <meta property="og:title" content={"Life  How it works"} />
              <link rel="canonical" href="https://be-lifer.com/how-it-works" />
              <meta property="og:site_name" content="Be-Lifer" />
            </Helmet>
      <div className="hiw-bg" />
      <div className="life-home__overlay" />

      {/* HERO */}
      <section className="life-section hiw-hero">
        <div className="life-container">
          <div className="hiw-heroGrid">
            {/* Left */}
            <div className="hiw-heroLeft">
              <div className="hiw-eyebrow">{hero?.eyebrow}</div>
              <h1 className="life-h1">{hero?.title}</h1>
              <p className="life-lead">{hero?.subtitle}</p>

              <div className="life-ctaRow">
                <a className="life-ctaPrimary" href={hero?.cta_primary?.href || "/contact"}>
                  {hero?.cta_primary?.label || t("fallback.cta_primary", "Accès en avant-première")}
                </a>
                <a className="hiw-ctaSecondary" href={hero?.cta_secondary?.href || "#steps"}>
                  {hero?.cta_secondary?.label || t("fallback.cta_secondary", "Voir le parcours")}
                </a>
              </div>

              {!!hero?.reassurance && <p className="life-ctaMeta">{hero.reassurance}</p>}

              {asArray(hero?.bullets).length > 0 && (
                <ul className="hiw-heroBullets" aria-label={t("a11y.hero_bullets", "Bénéfices clés")}>
                  {asArray(hero.bullets).map((b, idx) => (
                    <li key={idx} className="hiw-bullet">
                      <span className="hiw-dot" aria-hidden="true" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Right (image preview) */}
            <figure className="life-glass hiw-heroMedia" aria-label={t("a11y.preview", "Aperçu du système Life")}>
              <img
                className="hiw-heroImg"
                src={LifeBalancePhone}
                alt="Aperçu du cockpit Life : résumé, insights et trajectoire"
                loading="lazy"
              />
            </figure>
            {/* Macro overview (full-width) */}
            
          </div>
        </div>
      </section>
      <section className="life-section offer-steps">
        <div className="life-container">
          <div className="offer-sectionHeader">
            <h2 className="hiw-h2">{t("steps.title")}</h2>
            <p className="hiw-p muted">{t("steps.subtitle")}</p>
          </div>

          <div className="offer-stepsGrid">
            {[1, 2, 3, 4].map((n) => (
              <article key={n} className="life-glass offer-stepCard">
                <div className="offer-stepIndex">{String(n).padStart(2, "0")}</div>
                <div className="offer-stepTitle">{t(`steps.items.${n}.title`)}</div>
                <div className="offer-stepText">{t(`steps.items.${n}.text`)}</div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <div className="life-divider" />

      {/* STEPS */}
      <section id="steps" className="life-section hiw-steps">
        <div className="life-container">
          <div className="hiw-stepsGrid">
            {/* Left rail */}
            <aside className="hiw-rail" aria-label={t("a11y.nav", "Navigation du parcours")}>
              <div className="life-glass hiw-railInner">
                <div className="hiw-railTitle">{t("steps_section_title", "Le parcours Life")}</div>

                <nav className="hiw-nav">
                  {navItems.map((it, i) => (
                    <button
                      key={it.id}
                      className={`hiw-navItem ${i === activeIndex ? "isActive" : ""}`}
                      onClick={() => scrollToStep(i)}
                      type="button"
                    >
                      <span className="hiw-navIndex">{String(i + 1).padStart(2, "0")}</span>
                      <span className="hiw-navLabel">{it.label}</span>
                      <span className="hiw-navPip" aria-hidden="true" />
                    </button>
                  ))}
                </nav>

                <div className="hiw-progress" aria-hidden="true">
                  <div className="hiw-progressBar">
                    <div
                      className="hiw-progressFill"
                      style={{
                        height: steps.length <= 1 ? "0%" : `${(activeIndex / (steps.length - 1)) * 100}%`,
                      }}
                    />
                  </div>

                  <div className="hiw-progressMeta">
                    <div className="hiw-progressCaption">{t("progress.caption", "Avancement")}</div>
                    <div className="hiw-progressValue">
                      {steps.length ? `${activeIndex + 1}/${steps.length}` : "—"}
                    </div>
                  </div>
                </div>

                {!!simplicity?.title && asArray(simplicity?.points).length > 0 && (
                  <div className="hiw-simplicity">
                    <div className="hiw-simpTitle">{simplicity.title}</div>
                    <ul className="hiw-simpList">
                      {asArray(simplicity.points).map((p, idx) => (
                        <li key={idx}>{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </aside>

            {/* Right content */}
            <div className="hiw-cards" aria-label={t("a11y.step", "Étapes détaillées")}>
              {steps.length === 0 && (
                <div className="life-glass hiw-empty">
                  <h3 className="hiw-h3">{t("empty.title", "Contenu indisponible")}</h3>
                  <p className="hiw-p muted">
                    {t(
                      "empty.text",
                      "La clé 'steps' est vide ou le namespace 'howItWorks' n’est pas chargé pour cette langue."
                    )}
                  </p>
                </div>
              )}

              {steps.map((s, i) => (
                <article
                  key={s?.id || i}
                  className="life-glass hiw-card"
                  ref={(el) => (sectionRefs.current[i] = el)}
                  data-step-index={i}
                >
                  <header className="hiw-cardHeader">
                    <div className="hiw-stepMeta">
                      <div className="hiw-stepIndex">{String(i + 1).padStart(2, "0")}</div>
                      <div className="hiw-stepDivider" aria-hidden="true" />
                      <div className="hiw-stepTag">{s?.step_tag || t("fallback.step_tag", "Étape")}</div>
                    </div>

                    <h2 className="hiw-h2">{s?.short_title}</h2>
                    <p className="hiw-subtitle">{s?.benefit_subtitle}</p>
                  </header>

                  <div className="hiw-cardBody">
                    <div className="hiw-block">
                      <div className="hiw-blockTitle">{labels?.what_user_does || t("labels.what_user_does")}</div>
                      <p className="hiw-p">{s?.description_plain}</p>
                    </div>

                    <div className="hiw-split">
                      <div className="hiw-block">
                        <div className="hiw-blockTitle">{labels?.what_user_feels || t("labels.what_user_feels")}</div>
                        <p className="hiw-feel">“{s?.user_feeling}”</p>
                      </div>

                      <div className="hiw-block">
                        <div className="hiw-blockTitle">
                          {labels?.life_in_background || t("labels.life_in_background")}
                        </div>
                        <ul className="hiw-list">
                          {asArray(s?.life_background).map((x, idx) => (
                            <li key={idx}>{x}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="hiw-strategy">
                      <div className="hiw-blockTitle">{labels?.why_strategic || t("labels.why_strategic")}</div>
                      <p className="hiw-p">{s?.strategic_role}</p>
                    </div>
                  </div>
                </article>
              ))}

              {principles.length > 0 && (
                <section className="life-glass hiw-sectionCard">
                  <div className="hiw-sectionHeader">
                    <h3 className="hiw-h3">{principlesTitle}</h3>
                    {!!principlesSubtitle && <p className="hiw-p muted">{principlesSubtitle}</p>}
                  </div>
                  <div className="hiw-principlesGrid">
                    {principles.map((p, idx) => (
                      <div key={idx} className="hiw-principle">
                        <div className="hiw-principleTitle">{p?.title}</div>
                        <div className="hiw-principleText">{p?.text}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {week.length > 0 && (
                <section className="life-glass hiw-sectionCard">
                  <div className="hiw-sectionHeader">
                    <h3 className="hiw-h3">{weekTitle}</h3>
                    {!!weekSubtitle && <p className="hiw-p muted">{weekSubtitle}</p>}
                  </div>
                  <div className="hiw-weekGrid" aria-label={t("a11y.week", "Progression en 7 jours")}>
                    {week.map((d, idx) => (
                      <div key={idx} className="hiw-weekItem">
                        <div className="hiw-weekDay">{d?.day}</div>
                        <div className="hiw-weekTitle">{d?.title}</div>
                        <div className="hiw-weekText">{d?.text}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section className="hiw-closing">
                <div className="life-glass hiw-closingCard">
                  <h3 className="hiw-h3">{closing?.title || t("fallback.closing_title", "Structuré. Sérieux. Actionnable.")}</h3>
                  <p className="hiw-p">{closing?.text || ""}</p>

                  <div className="life-ctaRow">
                    <a className="life-ctaPrimary" href={closing?.cta_primary?.href || "#"}>
                      {closing?.cta_primary?.label || t("fallback.cta_primary", "Commencer l’essai")}
                    </a>
                    <a className="hiw-ctaSecondary" href={closing?.cta_secondary?.href || "#"}>
                      {closing?.cta_secondary?.label || t("fallback.cta_secondary", "Voir un exemple")}
                    </a>
                  </div>

                  {!!closing?.reassurance && <p className="life-ctaMeta">{closing.reassurance}</p>}
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
